import { objectToFormData } from "object-to-formdata";
import * as E from "fp-ts/lib/Either";
import {
  _Error,
  addNotificationToError,
  createErrorListFromErrorMessage,
  createErrorListFromUnknownError,
  logErrorsToUiStore
} from "./data";
import { iotsErrorsToCustomErrorType } from "@/util/io-ts-errors-to-_error";
import { Decoder } from "io-ts";
import { flow, pipe } from "fp-ts/lib/function";
import * as n from "@/types/notifications";
import { HttpStatusCode } from "@/types/errors";
import { right } from "fp-ts/Either";
import { flatten, isLeft, isRight, left, map, mapLeft } from "fp-ts/lib/Either";

type MakeWordPressAjaxRequestParams<I, A> = {
  action: string;
  overrideAjaxUrl?: string;
  dataToPost?: object;
  friendlyName?: string;
  processResults?: (response: {
    [key: string]: any;
  }) => E.Either<_Error[], unknown>;
  decoder: Decoder<I, A>;
  resultKey?: string;
  suppressErrorLogging?: boolean;
  abortController?: AbortController;
};

export type DecodedJson = { [key: string]: any };

const APPLICATION_JSON_CONTENT_TYPE = "application/json";

export async function makeWordPressAjaxRequest<I, A>(
  requestParams: MakeWordPressAjaxRequestParams<I, A>
): Promise<E.Either<_Error[], A>> {
  const friendlyName = requestParams.friendlyName || "WordPress AJAX Request";

  // This allows passing Eithers at requestParams to simplify this function's usage
  const errorsFromDataToPostEithers: _Error[] = [];
  const requestParamsDataToPostWithEithersResolved: Record<any, unknown> = {};
  for (const [k, v] of Object.entries(requestParams.dataToPost || {})) {
    if (v && isLeft(v)) {
      errorsFromDataToPostEithers.push(...(v.left as _Error[]));
    } else if (v && isRight(v)) {
      requestParamsDataToPostWithEithersResolved[k] = v.right;
    } else {
      requestParamsDataToPostWithEithersResolved[k] = v;
    }
  }
  if (errorsFromDataToPostEithers.length) {
    if (requestParams.suppressErrorLogging) {
      return left(errorsFromDataToPostEithers);
    } else {
      logErrorsToUiStore(errorsFromDataToPostEithers);
      return left([]);
    }
  }

  const dataToPost = requestParams.overrideAjaxUrl
    ? {
        ...requestParamsDataToPostWithEithersResolved,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        breakdance_ajax_at_any_url: "true"
      }
    : requestParamsDataToPostWithEithersResolved;

  const payload = { action: requestParams.action, ...dataToPost };

  const response = await _makeWordPressAjaxRequest(
    payload,
    requestParams.overrideAjaxUrl,
    friendlyName,
    requestParams.abortController,
  );

  const result = pipe(
    response,
    E.fold(
      (e: _Error[]) => E.left(e),
      response => {
        let toDecode;

        if (requestParams.resultKey) {
          toDecode = response[requestParams.resultKey];
        } else {
          toDecode = response;
        }

        const res = pipe(
          toDecode,
          v =>
            requestParams.processResults
              ? requestParams.processResults(v)
              : right(v),
          map(
            flow(
              requestParams.decoder.decode,
              mapLeft(iotsErrorsToCustomErrorType)
            )
          ),
          flatten
        );

        return res;
      }
    )
  );

  if (isLeft(result)) {
    if (requestParams.suppressErrorLogging) {
      return result;
    } else {
      logErrorsToUiStore(result.left);
      return E.left([]);
    }
  }

  return result;
}

function getResponseContentType(response: Response): string | null {
  return response.headers.get("Content-Type");
}

async function decodeResponse(
  response: Response
): Promise<E.Either<_Error[], DecodedJson | string>> {
  const contentType = getResponseContentType(response);

  if (contentType?.includes(APPLICATION_JSON_CONTENT_TYPE)) {
    try {
      const decodedJson = await response.json();
      return E.right(decodedJson);
    } catch (e) {
      return left(
        createErrorListFromUnknownError(
          e,
          "Unable to decode JSON response body"
        )
      );
    }
  } else {
    try {
      const decodedText = await response.text();
      return E.right(decodedText);
    } catch (e) {
      return left(
        createErrorListFromUnknownError(e, "Unable to decode response body")
      );
    }
  }
}

function isDecodedResponseJson(
  decodedResponse: DecodedJson | string
): decodedResponse is DecodedJson {
  return typeof decodedResponse !== "string";
}

// TODO: how long can this function run before the AJAX request times out?
// we dont want the user to get a spiny wheel on the UI for a whole minute
async function _makeWordPressAjaxRequest(
  dataToPost: object,
  overrideAjaxUrl: false | string = false,
  friendlyName: string,
  abortController: AbortController | null = null,
): Promise<E.Either<_Error[], DecodedJson>> {
  const url = new URL(
    overrideAjaxUrl ? overrideAjaxUrl : window.Breakdance.ajaxurl
  );

  url.searchParams.append("_breakdance_doing_ajax", "yes");

  const successfulHttpCodes = [HttpStatusCode.OK, HttpStatusCode.CREATED];

  const createNetworkErrorNotification = (
    message: string,
    requestDetails: n.RequestDetails
  ): n.NetworkErrorNotification => ({
    type: "NETWORK_ERROR",
    errorDetails: {
      friendlyName,
      message
    },
    requestDetails
  });

  if (window.Breakdance.ajaxnonce) {
    url.searchParams.set("_ajax_nonce", window.Breakdance.ajaxnonce);
  }

  const requestDetails = {
    friendlyName: friendlyName,
    responseCode: "-1",
    requestUrl: url.toString(),
    requestMethod: "POST",
    body: dataToPost,
    requestTimeGmt: new Date().toUTCString()
  };

  try {
    const response = await _timeableFetch(url.toString(), dataToPost, abortController);
    const responseStatusCode = response.status;
    if (response.headers.has("Breakdance-Refresh-Nonce")) {
      const updatedNonce = response.headers.get("Breakdance-Refresh-Nonce");
      if (updatedNonce !== null) {
        window.Breakdance.ajaxnonce = updatedNonce;
      }
    }
    const maybeDecodedResponse = await decodeResponse(response);

    requestDetails.responseCode = responseStatusCode.toString();

    // Successful response body decoding is an absolute requirement before proceeding
    if (E.isLeft(maybeDecodedResponse)) {
      const responseDecodingErrors = maybeDecodedResponse.left;
      return E.left(
        responseDecodingErrors.map(decodingError =>
          addNotificationToError(
            decodingError,
            createNetworkErrorNotification(decodingError.msg, requestDetails)
          )
        )
      );
    }

    const decodedResponse = maybeDecodedResponse.right;

    if (successfulHttpCodes.includes(responseStatusCode)) {
      // For succeeded AJAX requests we only expect JSON to be returned from server
      if (isDecodedResponseJson(decodedResponse)) {
        return E.right(decodedResponse);
      }
      const errorMessage = `Unexpected response type, expected "${APPLICATION_JSON_CONTENT_TYPE}", got "${getResponseContentType(
        response
      )}"`;

      return left(
        createErrorListFromErrorMessage(
          errorMessage,
          createNetworkErrorNotification(errorMessage, requestDetails)
        )
      );
    } else if (responseStatusCode === HttpStatusCode.UNAUTHORIZED) {
      return left(
        createErrorListFromErrorMessage("Logged out", {
          type: "LOGGED_OUT",
          message: friendlyName
        })
      );
    } else if (responseStatusCode === HttpStatusCode.EXPIRED) {
      return left(
        createErrorListFromErrorMessage("Session Expired", {
          type: "SESSION_EXPIRED",
          message: friendlyName
        })
      );
    } else if (responseStatusCode === HttpStatusCode.FORBIDDEN) {
      const errorMessage = isDecodedResponseJson(decodedResponse)
        ? decodedResponse?.error?.message
        : undefined;
      return left(
        createErrorListFromErrorMessage(errorMessage, {
          type: "FORBIDDEN",
          requestDetails,
          errorMessage
        })
      );
    } else if (
      responseStatusCode === HttpStatusCode.INTERNAL_SERVER_ERROR ||
      responseStatusCode === HttpStatusCode.BAD_REQUEST
    ) {
      const internalServerErrorNotification: n.InternalServerErrorNotification =
        {
          type: "INTERNAL_SERVER_ERROR",
          errorDetails: isDecodedResponseJson(decodedResponse)
            ? decodedResponse.error
            : decodedResponse,
          errorMessage: isDecodedResponseJson(decodedResponse)
            ? undefined
            : decodedResponse,
          requestDetails
        };
      return left(
        createErrorListFromErrorMessage(
          "Internal Server Error",
          internalServerErrorNotification
        )
      );
    } else {
      const errorMessage = isDecodedResponseJson(decodedResponse)
        ? decodedResponse.error
        : decodedResponse;

      const ajaxCodeErrorNotification: n.AjaxCodeErrorNotification = {
        type: "AJAX_CODE_ERROR",
        errorDetails: {
          message: errorMessage
        },
        requestDetails
      };
      return left(
        createErrorListFromErrorMessage(errorMessage, ajaxCodeErrorNotification)
      );
    }
  } catch (errorPayload) {
    return left(
      createErrorListFromUnknownError(
        errorPayload,
        "Unknown network error",
        (m: string) => createNetworkErrorNotification(m, requestDetails)
      )
    );
  }
}

async function _timeableFetch(
  url: string,
  dataToPost: object,
  abortController: AbortController | null
): Promise<Response> {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        body: objectToFormData(dataToPost, { indices: true }),
        headers: {
          "X-Requested-With": "BreakdanceXMLHttpRequest"
        },
        signal: abortController ? abortController.signal : undefined
      });

      resolve(response);
    } catch (error) {
      reject({ error });
    }
  });
}
